/**
 * `messages:` / `m:` (PLAN §2.2, S7 client, §12.3.2): chat message text across
 * all of the user's CURRENT conversations, searched by the server. A server
 * provider: ≥ 3 characters, 300 ms debounce, aborted by the next keystroke.
 * In global search it is not run; a "Search messages for …" row enters this
 * scope instead (inGlobal: false). A 404 (older server) turns the provider
 * off for the session — no error row. Enter opens the conversation and jumps
 * to the message.
 */
import { TextSearch, CornerDownLeft, Copy } from 'lucide-react';
import { ORDER } from './order';
import { copyWithNotice } from '../actions';
import { isAbortError } from '../types';
import i18n from '../../i18n';

export const MESSAGES_MIN_CHARS = 3;
export const MESSAGES_DEBOUNCE_MS = 300;

/** Map one §12.3.2 result to a palette Result (§12.3.2 "Client mapping"). */
export function messageResult(item, i, n, meta) {
  const m = item.message || {};
  const ch = item.channel || {};
  const conv = ch.kind === 'dm' ? ch.dm_other?.name || i18n.t('palette.people.directMessage') : ch.kind === 'channel' ? `#${ch.name || i18n.t('palette.fallback.channel')}` : ch.name || i18n.t('palette.fallback.group');
  const ts = m.inserted_at ? Date.parse(m.inserted_at) : undefined;
  return {
    provider: 'messages',
    id: String(m.id ?? `${ch.id}:${m.server_seq}`),
    title: m.sender?.name || i18n.t('palette.fallback.person'),
    subtitle: conv,
    snippet: item.snippet && typeof item.snippet.text === 'string' ? { text: item.snippet.text, ranges: item.snippet.ranges || [] } : undefined,
    ts: Number.isFinite(ts) ? ts : undefined,
    baseScore: n > 0 ? 1 - i / n : 0,
    avatarUserId: m.sender_id != null ? String(m.sender_id) : undefined,
    payload: { channelId: ch.id ?? m.channel_id, seq: m.server_seq ?? null, body: m.body ?? '', meta },
  };
}

/**
 * @param {{ search: (q: object, opts: {signal}) => Promise<object>,
 *           openMessage: (channelId, seq) => Promise<void>|void }} deps
 */
export default function create(deps) {
  let unavailable = false;
  return {
    id: 'messages',
    prefixes: ['messages', 'm', 'message', 'msg'],
    labelKey: 'palette.scope.messages',
    icon: TextSearch,
    order: ORDER.messages,
    page: 'chat.panel',
    kind: 'server',
    debounceMs: MESSAGES_DEBOUNCE_MS,
    minChars: MESSAGES_MIN_CHARS,
    inGlobal: false,
    available: (ctx) => !unavailable && !!ctx.currentUserId,
    async search(req) {
      if (!req.ctx.online) return [];
      let body;
      try {
        body = await deps.search({ q: req.text, limit: Math.min(50, req.limit) }, { signal: req.signal });
      } catch (err) {
        if (isAbortError(err)) throw err;
        if (err?.response?.status === 404) {
          unavailable = true;
          return [];
        }
        throw err;
      }
      const list = Array.isArray(body?.results) ? body.results : [];
      const meta = {
        partial: !!body?.partial,
        indexComplete: body?.index_complete !== false,
        historyDays: body?.history_days ?? null,
        complete: body?.complete !== false,
      };
      return list.map((item, i) => messageResult(item, i, list.length, meta));
    },
    actions() {
      return [
        { id: 'open', titleKey: 'palette.action.openMessage', icon: CornerDownLeft, run: (r) => deps.openMessage(r.payload.channelId, r.payload.seq) },
        { id: 'copyText', titleKey: 'palette.action.copyText', icon: Copy, run: (r) => copyWithNotice(r.payload.body, i18n.t('palette.notice.message')) },
      ];
    },
    /** Tests: forget a 404. */
    resetAvailability() {
      unavailable = false;
    },
  };
}
